// Recording a car sale — used by BOTH the superadmin panel and the cars team.
//
// On a commission car the split is shown BEFORE confirming. Whoever is at the
// keyboard should see "Kunja gets ₹9,00,000 · CAP keeps ₹45,000" while there is
// still a Cancel button, not discover it afterwards on a detail screen.

import React, { useEffect, useState } from 'react';
import { Alert, Button } from '../common/index.js';
import { Field, Modal, NumberInput, Select, errText, fmtMoney, inputCls } from './controls.jsx';
import { CAR_PAYMENT_MODE_OPTIONS, MODE_NEEDS_BANK } from '../../constants/paymentModes';

const today = () => new Date().toISOString().slice(0, 10);

/** The same maths the backend freezes onto the sale, run locally for preview. */
export const previewSplit = (car, soldPrice) => {
  const price = Number(soldPrice || 0);
  if (!price || car?.ownership !== 'consignment') return null;
  let commission = 0;
  if (car.commission_type === 'fixed') commission = Number(car.commission_value || 0);
  else if (car.commission_type === 'percent') commission = price * Number(car.commission_value || 0) / 100;
  else if (car.commission_type === 'margin') commission = price - Number(car.owner_price || 0);
  commission = Math.max(Math.min(commission, price), 0);
  return { commission, owner: price - commission };
};

// `intent` is which door was opened — 'sell' (paid in full) or 'book' (a
// token holds it). Both land here because they are the same record; the door
// just decides what the form assumes first. Either can still be switched.
const SellCarModal = ({ open, onClose, car, onSold, sellFn, intent = 'sell' }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      customer_name: '',
      customer_phone: '',
      sold_price: car?.asking_price ? String(car.asking_price) : '',
      sold_at: today(),
      payment_type: intent === 'book' ? 'advance' : 'full',
      payment_mode: 'cash',
      bank_name: '',
      advance_amount: '',
      notes: '',
    });
    setError('');
  }, [open, car, intent]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const advance = form.payment_type === 'advance';
  const consignment = car?.ownership === 'consignment';
  const preview = previewSplit(car, form.sold_price);
  const needsBank = MODE_NEEDS_BANK.has(form.payment_mode);

  const submit = async () => {
    setError('');
    if (!form.sold_price || Number(form.sold_price) <= 0) {
      setError('What did it sell for?');
      return;
    }
    if (form.payment_mode === 'loan' && !form.bank_name?.trim()) {
      setError('Which bank or financer is the loan with?');
      return;
    }
    setBusy(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== '' && v !== null),
      );
      const { data } = await sellFn(car.id, payload);
      onSold(data);
      onClose();
    } catch (e) {
      setError(errText(e, 'Could not record the sale.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${advance ? 'Book' : 'Sell'} ${car?.display_name || 'this car'}`}
      subtitle={consignment ? `${car.owner_name}'s car · ${car.commission_label}` : 'Our car'}
    >
      {error && <Alert type="error" message={error} className="mb-4" />}
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Buyer's name" required>
            <input type="text" value={form.customer_name || ''} onChange={(e) => set('customer_name')(e.target.value)} placeholder="Ramesh" className={inputCls} />
          </Field>
          <Field label="Buyer's phone" hint="Links them to the same customer record as the parts side.">
            <input type="text" value={form.customer_phone || ''} onChange={(e) => set('customer_phone')(e.target.value)} placeholder="10-digit mobile number" className={inputCls} />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Sold for" required>
            <NumberInput value={form.sold_price} onChange={set('sold_price')} placeholder="520000" />
          </Field>
          <Field label="On" required>
            <input type="date" value={form.sold_at || ''} onChange={(e) => set('sold_at')(e.target.value)} className={inputCls} />
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Payment">
            <Select
              value={form.payment_type}
              onChange={set('payment_type')}
              options={[
                { value: 'full', label: 'Paid in full' },
                { value: 'advance', label: 'Token / advance', hint: 'The rest is still owed' },
              ]}
            />
          </Field>
          <Field label="Mode">
            <Select
              value={form.payment_mode}
              onChange={set('payment_mode')}
              options={CAR_PAYMENT_MODE_OPTIONS}
            />
          </Field>
        </div>
        {/* A loan is chased at a bank — take its name with the money. */}
        {needsBank && (
          <Field
            label={form.payment_mode === 'loan' ? 'Which bank / financer?' : 'Which bank?'}
            required={form.payment_mode === 'loan'}
            hint={form.payment_mode === 'loan'
              ? 'HDFC, Mahindra Finance… whoever the loan is with'
              : 'Optional — whose account it came from'}
          >
            <input
              type="text"
              value={form.bank_name || ''}
              onChange={(e) => set('bank_name')(e.target.value)}
              placeholder="HDFC Bank"
              className={inputCls}
            />
          </Field>
        )}
        {advance && (
          <Field label="Taken now" required hint="The balance stays on the list until it is collected.">
            <NumberInput value={form.advance_amount} onChange={set('advance_amount')} placeholder="50000" />
          </Field>
        )}

        {preview && (
          <div className="rounded-xl border border-violet-200 bg-violet-50 p-3 text-sm">
            <p className="text-xs font-bold uppercase tracking-wide text-violet-500">How this splits</p>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-slate-600">{car.owner_name} gets</span>
              <span className="font-bold tabular-nums text-slate-800">{fmtMoney(preview.owner)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <span className="font-semibold text-violet-700">CAP keeps</span>
              <span className="font-black tabular-nums text-violet-700">{fmtMoney(preview.commission)}</span>
            </div>
            {preview.commission === 0 && (
              <p className="mt-2 text-[11px] font-semibold text-rose-600">
                At this price CAP earns nothing on the car.
              </p>
            )}
          </div>
        )}

        <Field label="Notes">
          <textarea rows={2} value={form.notes || ''} onChange={(e) => set('notes')(e.target.value)} className={inputCls} />
        </Field>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy}>{advance ? 'Book the car' : 'Record the sale'}</Button>
      </div>
    </Modal>
  );
};

export default SellCarModal;
